import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { Card, TwoLineData, TwoLineGrid } from '~components';
import getInteractiveBatteryIconData from '~components/icons/interactiveBatteryData';
import { dateFormats } from '~constants';
import DevicesService from '~features/devices/services/devicesService';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';

interface DeviceStateCardProps {
	deviceId?: string | undefined;
	organisationId: string | undefined;
	isLoading?: boolean;
}

const deviceService = getService(DevicesService);

const DeviceStateCard = ({ deviceId, organisationId, isLoading = false }: DeviceStateCardProps) => {
	const { t } = useTranslation();
	// Load device
	const {
		data: deviceData,
		error: _deviceError,
		isLoading: deviceIsLoading,
	} = useSWR(deviceService.getDeviceByIdCacheKey(deviceId, organisationId), ([_, id, orgId]) => {
		if (id == undefined) return;
		return deviceService.getDeviceById(id, orgId ?? undefined);
	});

	const resolvedLoading = isLoading || deviceIsLoading;
	const batteryData = getInteractiveBatteryIconData(deviceData?.state?.batteryState, t);

	return (
		<Card
			title={t('device.labels.deviceState', { ns: 'entities' })}
			caption={
				deviceData?.state?.lastReport ?
					`${t('device.labels.lastUpdated', { ns: 'entities' })}: ${deviceData?.state?.lastReport?.format(dateFormats.dateTime)}`
				:	undefined
			}
		>
			<TwoLineGrid
				isLoading={resolvedLoading}
				colSize={{ xs: 12, sm: 6, lg: 4 }}
				rowSpacing={Dimens.spacing4xl}
				twoLineItems={[
					{
						primary: t('device.labels.lockState', { ns: 'entities' }),
						secondary: deviceData?.state?.lockState,
						secondarySize: 'large',
						icon: 'icon/lock',
					},
					{
						primary: t('deviceState.labels.temperature', { ns: 'entities' }),
						secondary: `${deviceData?.state?.temperature?.toFixed(1)} °C`,
						secondarySize: 'large',
						icon: 'icon/temperature',
						isHidden: deviceData?.state?.temperature == undefined && !isLoading,
					},
					{
						primary: t('device.fields.battery', { ns: 'entities' }),
						secondary:
							deviceData?.state?.batteryState ?
								`${deviceData?.state?.batteryState}%`
							:	batteryData.label,
						secondarySize: 'large',
						icon: resolvedLoading ? 'icon/dots-vertical' : batteryData.icon,
						framedIconVariant: batteryData.variant,
					},
					...(deviceData?.state?.connectivityProfile != undefined ?
						[
							{
								primary: t('device.labels.connectivityProfile', { ns: 'entities' }),
								secondary: t(deviceData.state.connectivityProfile.name, { ns: 'entities' }),
								secondarySize: 'large',
								icon: resolvedLoading ? 'icon/dots-vertical' : 'icon/globe',
							} as TwoLineData,
						]
					:	[]),
				]}
			/>
		</Card>
	);
};

export default DeviceStateCard;
